import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Alert, Spinner } from "react-bootstrap";
import config from "../../../../../config/config.json";
import { apiCall } from "../../../../../_services/apiCall";
import "./UploadBRSRModal.css"

const UploadBRSRModal = ({ show, onClose }) => {
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  // Fetch upload history when modal opens
  useEffect(() => {
    if (show) {
      fetchUploadHistory();
    }
  }, [show]);

  // Poll status for pending/processing documents
  useEffect(() => {
    if (!show) return;

    const pendingDocs = uploadHistory.filter(
      (doc) => doc.status === "PENDING" || doc.status === "PROCESSING"
    );

    if (pendingDocs.length === 0) return;

    const interval = setInterval(() => {
      pendingDocs.forEach((doc) => {
        pollDocumentStatus(doc.id);
      });
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [uploadHistory, show]);

  const fetchUploadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}brsr-processing`,
        {},
        {},
        "GET"
      );

      if (isSuccess) {
        setUploadHistory(data || []);
      } else {
        setError("Failed to fetch upload history");
      }
    } catch (err) {
      console.error("Error fetching upload history:", err);
      setError("An error occurred while fetching upload history");
    } finally {
      setLoading(false);
    }
  };

  const pollDocumentStatus = async (docId) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}brsr-processing/status/${docId}`,
        {},
        {},
        "GET",
        true,
        false
      );

      if (isSuccess && data) {
        setUploadHistory((prev) =>
          prev.map((doc) =>
            doc.id === docId ? { ...doc, status: data.status, errorMessage: data.errorMessage } : doc
          )
        );
      }
    } catch (err) {
      console.error("Error polling document status:", err);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (PDF, Excel, etc.)
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a valid file (PDF or Excel)");
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file to upload");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}brsr-processing/parse`,
        {},
        formData,
        "POST"
      );

      if (isSuccess) {
        // Add the new upload to the beginning of the list
        const newUpload = {
          id: data?.pollId,
          documentProcessing: {
            rawFileMetadata: {
              fileName: selectedFile.name,
            },
          },
          status: "PENDING",
          createdAt: new Date().toISOString(),
          provider: "BRSR",
        };

        setUploadHistory((prev) => [newUpload, ...prev]);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Show success message
        setError(null);
      } else {
        setError(data?.message || "Failed to upload file");
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("An error occurred while uploading the file");
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: "warning", text: "Pending" },
      PROCESSING: { variant: "info", text: "Processing" },
      COMPLETED: { variant: "success", text: "Completed" },
      FAILED: { variant: "danger", text: "Failed" },
    };

    const config = statusConfig[status] || { variant: "secondary", text: status };

    return (
      <span className={`badge bg-${config.variant}`}>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ color: "#3f88a5", fontWeight: "bold" }}>
          Upload BRSR
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "400px" }}>
        {/* Upload Section */}
        <div className="upload-section mb-4 p-3" style={{ 
          border: "2px dashed #3f88a5", 
          borderRadius: "10px",
          backgroundColor: "#f8f9fa"
        }}>
          <h5 style={{ color: "#3f88a5", marginBottom: "15px" }}>Upload New BRSR</h5>
          
          <div className="d-flex align-items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.xlsx,.xls"
              className="form-control"
              style={{ 
                border: "2px solid #3f88a5",
                borderRadius: "8px",
                maxWidth: "400px"
              }}
              disabled={uploading}
            />
            
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="esg_button_style"
              style={{
                minWidth: "120px",
                backgroundColor: "#3f88a5",
                border: "none",
              }}
            >
              {uploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fa fa-upload me-2"></i>
                  Upload
                </>
              )}
            </Button>
          </div>

          {selectedFile && (
            <div className="mt-2" style={{ color: "#666", fontSize: "14px" }}>
              Selected: {selectedFile.name}
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Upload History Section */}
        <div className="history-section">
          <h5 style={{ color: "#3f88a5", marginBottom: "15px" }}>
            Upload History
          </h5>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: "#3f88a5" }} />
              <p className="mt-2" style={{ color: "#666" }}>Loading history...</p>
            </div>
          ) : uploadHistory.length === 0 ? (
            <div className="text-center py-5" style={{ color: "#666" }}>
              <i className="fa fa-file-text fa-3x mb-3" style={{ opacity: 0.3 }}></i>
              <p>No BRSR uploads yet</p>
            </div>
          ) : (
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <Table hover responsive>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
                  <tr>
                    <th style={{ color: "#3f88a5" }}>File Name</th>
                    <th style={{ color: "#3f88a5" }}>Status</th>
                    <th style={{ color: "#3f88a5" }}>Uploaded On</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadHistory.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <i className="fa fa-file-pdf me-2" style={{ color: "#3f88a5" }}></i>
                        {doc.documentProcessing?.rawFileMetadata?.fileName || "Unknown"}
                      </td>
                      <td>
                        {getStatusBadge(doc.status)}
                        {doc.status === "PROCESSING" && (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            className="ms-2"
                            style={{ color: "#3f88a5" }}
                          />
                        )}
                        {doc.status === "FAILED" && doc.errorMessage && (
                          <div className="text-danger small mt-1">
                            {doc.errorMessage}
                          </div>
                        )}
                      </td>
                      <td>{formatDate(doc.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadBRSRModal;